// 渲染与内存缓存：解码过的底图/淡妆脸/结果图层都放在 LRU 里，切换发型只做一次合成。

import { applyBeauty, applyTone, type BeautyLevel, faceLuma, toneFor } from "./beauty";
import { type Photo, type Result, unpackAnalysis } from "./db";
import { blobToCanvas, canvasToBlob, maskToCanvas, newCanvas } from "./image";
import { composite } from "./lock";
import type { Analysis } from "./vision";

class LRU<V> {
  private map = new Map<string, V>();
  constructor(private limit: number) {}
  get(k: string): V | undefined {
    const v = this.map.get(k);
    if (v !== undefined) {
      this.map.delete(k);
      this.map.set(k, v);
    }
    return v;
  }
  set(k: string, v: V) {
    this.map.delete(k);
    this.map.set(k, v);
    while (this.map.size > this.limit) this.map.delete(this.map.keys().next().value as string);
  }
  deletePrefix(prefix: string) {
    for (const k of [...this.map.keys()]) if (k.startsWith(prefix)) this.map.delete(k);
  }
}

const baseCache = new LRU<Promise<{ canvas: HTMLCanvasElement; analysis: Analysis }>>(6);
const faceCache = new LRU<Promise<HTMLCanvasElement>>(8);
const layerCache = new LRU<Promise<{ aligned: HTMLCanvasElement; mask: HTMLCanvasElement }>>(14);
const lumaCache = new LRU<Promise<number>>(8);

function getTone(photo: Photo, level: BeautyLevel) {
  let p = lumaCache.get(photo.id);
  if (!p) {
    p = getBase(photo).then(({ canvas, analysis }) => faceLuma(canvas, analysis));
    lumaCache.set(photo.id, p);
  }
  return p.then((m) => toneFor(level, m));
}

export function getBase(photo: Photo) {
  let p = baseCache.get(photo.id);
  if (!p) {
    p = blobToCanvas(photo.base).then((canvas) => ({ canvas, analysis: unpackAnalysis(photo.analysis) }));
    baseCache.set(photo.id, p);
  }
  return p;
}

/** 某张照片在某个妆容等级下的脸（整张图，合成时只取脸部区域） */
export function getFace(photo: Photo, level: BeautyLevel) {
  const key = `${photo.id}|${level}`;
  let p = faceCache.get(key);
  if (!p) {
    p = getBase(photo).then(({ canvas, analysis }) => applyBeauty(canvas, analysis, level));
    faceCache.set(key, p);
  }
  return p;
}

function getLayers(r: Result) {
  let p = layerCache.get(r.id);
  if (!p) {
    p = blobToCanvas(r.aligned).then((aligned) => ({
      aligned,
      mask: maskToCanvas(r.mask, r.mw, r.mh, aligned.width, aligned.height),
    }));
    layerCache.set(r.id, p);
  }
  return p;
}

export function forgetPhoto(photoId: string) {
  baseCache.deletePrefix(photoId);
  faceCache.deletePrefix(photoId);
  lumaCache.deletePrefix(photoId);
  layerCache.deletePrefix(photoId);
}

export function forgetResult(id: string) {
  layerCache.deletePrefix(id);
}

/** 预热：提前解码，切换时更快 */
export function prefetch(r: Result) {
  void getLayers(r);
}

export async function renderResult(target: HTMLCanvasElement, photo: Photo, r: Result, level: BeautyLevel) {
  const [face, layers, tone] = await Promise.all([getFace(photo, level), getLayers(r), getTone(photo, level)]);
  composite(target, layers.aligned, face, layers.mask);
  applyTone(target, tone);
}

export async function renderPhoto(target: HTMLCanvasElement, photo: Photo, level: BeautyLevel) {
  const [face, tone] = await Promise.all([getFace(photo, level), getTone(photo, level)]);
  if (target.width !== face.width || target.height !== face.height) {
    target.width = face.width;
    target.height = face.height;
  }
  target.getContext("2d")!.drawImage(face, 0, 0);
  applyTone(target, tone);
}

/** 生成完成时做一张 360px 的缩略图存起来，列表里不用再解码大图 */
export async function renderThumb(
  base: HTMLCanvasElement,
  _analysis: Analysis,
  aligned: HTMLCanvasElement,
  mask: Uint8Array,
  mw: number,
  mh: number,
): Promise<Blob> {
  const tw = 360;
  const th = Math.round((base.height / base.width) * tw);
  const small = (src: HTMLCanvasElement) => {
    const c = newCanvas(tw, th);
    const g = c.getContext("2d")!;
    g.imageSmoothingQuality = "high";
    g.drawImage(src, 0, 0, tw, th);
    return c;
  };
  const out = newCanvas(tw, th);
  composite(out, small(aligned), small(base), maskToCanvas(mask, mw, mh, tw, th));
  return canvasToBlob(out, "image/jpeg", 0.82);
}

/** 导出当前画面为可保存的图片 */
export async function exportImage(photo: Photo, r: Result | null, level: BeautyLevel): Promise<Blob> {
  const c = newCanvas(photo.width, photo.height);
  if (r) await renderResult(c, photo, r, level);
  else await renderPhoto(c, photo, level);
  return canvasToBlob(c, "image/jpeg", 0.92);
}
